/**
 * The in-app follow-up on a feedback ticket (owner, 2026-10-16: "most of
 * them do not check the email... add follow-ups into the frontend"): the
 * team's replies and the person's answers, read and written through
 * /api/feedback-thread (backend routes/feedback_offer.py).
 */
import { API_BASE, fetchAPI } from "@/lib/api";
import { invalidateShared, shared } from "@/lib/shared-get";

export interface ThreadMessage {
  id: number;
  author: "team" | "user";
  body: string;
  created_at: string;
  read_at: string | null;
  /** A team message that asks something; false for "your idea is live". */
  needs_reply: boolean;
}

export interface ThreadCounts {
  /** Conversations that want the person's eye: a question unanswered, a reply unread. */
  attention: number;
  open: number;
}

export interface ThreadState {
  ticket: { id: number; subject: string; category: string; created_at: string; message: string } | null;
  messages: ThreadMessage[];
  /** The team asked last and the person has not answered: the card stays. */
  awaiting_user: boolean;
  /** The team's last word only informs and has not been opened: the card shows once. */
  unseen_reply: boolean;
  /** Over every conversation of the account (the account menu's number). */
  counts?: ThreadCounts;
}

export type ThreadStateName = "awaiting_user" | "awaiting_team" | "answered" | "closed";

/** One conversation in the list on the contact page. */
export interface ThreadSummary {
  id: number;
  subject: string;
  category: string;
  status: string;
  created_at: string;
  last_at: string;
  last_author: "team" | "user" | null;
  last_excerpt: string;
  message_count: number;
  state: ThreadStateName;
  unread: number;
}

export interface ThreadListState {
  threads: ThreadSummary[];
  counts: ThreadCounts;
}

/** Fired on window when an answer went out, so the card re-reads the thread. */
export const THREAD_CHANGED_EVENT = "fbt:changed";
/** Fired on window to open one conversation's sheet (the contact page's list). */
export const THREAD_OPEN_EVENT = "fbt:open";

/** Shared read: the corner card asks on every page. With `seen` the
 *  team's messages count as read (the sheet is open). With `ticketId`
 *  one conversation picked from the list, else the newest with a thread. */
export function loadThread(seen = false, ticketId?: number): Promise<ThreadState> {
  if (seen || ticketId != null) {
    const params: Record<string, string> = {};
    if (seen) params.seen = "1";
    if (ticketId != null) params.ticket_id = String(ticketId);
    return fetchAPI<ThreadState>("/feedback-thread", params);
  }
  return shared("/feedback-thread", () => fetchAPI<ThreadState>("/feedback-thread"), { reuseMs: 60_000 });
}

/** Every conversation of the account (the contact page). */
export function loadThreads(): Promise<ThreadListState> {
  return shared("/feedback-threads", () => fetchAPI<ThreadListState>("/feedback-threads"), { reuseMs: 15_000 });
}

/** Forget the cached reads and tell every reader (the corner card, the
 *  contact page's list, the account menu's number) to ask again. */
export function refreshThreads(): void {
  invalidateShared("/feedback-thread"); // the prefix covers /feedback-threads too
  try { window.dispatchEvent(new Event(THREAD_CHANGED_EVENT)); } catch { /* no window */ }
}

export async function replyThread(ticketId: number, body: string): Promise<ThreadState> {
  const res = await fetch(`${API_BASE}/feedback-thread`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ticket_id: ticketId, body }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(typeof data.detail === "string" ? data.detail : `HTTP ${res.status}`);
  }
  refreshThreads();
  return res.json();
}
